<template>
    <div class="cic-date-picker">
        <el-date-picker
            v-if="enabled"
            :model-value="currentValue"
            :type="type"
            :picker-options="pickerOptions"
            v-bind="Object.assign(JSON.parse(JSON.stringify($attrs)), { id: undefined })"
            style="width: fit-content"
            @update:modelValue="handleChange"
        />
        <span v-else>{{ value.format('YYYY-MM-DD HH:mm:ss') }}</span>
    </div>
</template>

<script>
    import moment from 'moment';
    import { ElDatePicker } from 'element-plus';

    /**
     * Convert a Date to a moment object
     *
     * @param localDateTime - The Date to convert
     * @param type          - Whether we're handling a date or a datetime
     */
    const dateToMoment = function (localDateTime, type) {
        if (localDateTime === null) {
            return null;
        }

        if (type === 'date') {
            // We're only interested in the date. If we were to convert this directly then when the date is converted to
            // UTC before being sent to the server, if we're on BST (for example) then the date will be changed to 2300
            // the previous day. To work around this we create a UTC moment object directly, using just the date part of
            // localDateTime.

            // First, create a moment object from localDateTime so that we can grab just the date part.
            let localDateTimeMoment = moment(localDateTime);

            // Turn the localDateTimeMoment into a string representation of just the date and use this to create a new
            // UTC moment object.
            let formattedValue = localDateTimeMoment.format('YYYY-MM-DD');
            return moment.utc(formattedValue, 'YYYY-MM-DD', true);
        } else {
            // Parse the string datetime into a non UTC moment instance and emit it. Since this has a time part to it
            // we're interested in preserving any timezone adjustments.
            return moment(localDateTime, 'YYYY-MM-DD HH:mm:ss', true);
        }
    };

    export default {
        name: 'CicDatePicker',
        components: {
            'el-date-picker': ElDatePicker,
        },
        props: {
            /* eslint-disable vue/require-default-prop */
            // TODO - decide which props are required & defaults then re-enable this rule
            value: {
                type: Object,
                validator: function (value) {
                    // Ensure the object we've been passed is a moment instance
                    return moment.isMoment(value);
                },
            },
            type: {
                type: String,
            },
            enabled: {
                type: Boolean,
                default: true,
            },
            validDates: {
                type: Array,
                default: () => [],
            },
        },
        emits: ['input'],
        data: function () {
            return {
                pickerOptions: {
                    disabledDate: (date) => {
                        // If validDate array is empty then just return false to make all dates valid
                        if (this.validDates.length === 0) {
                            return false;
                        }

                        // If date is in validDates array then return false to keep the date enabled
                        let formattedDate = moment(date).format('YYYY-MM-DD');
                        for (date of this.validDates) {
                            if (date === formattedDate) {
                                return false;
                            }
                        }

                        // The date isn't in the list of valid dates, so don't allow it to be selected
                        return true;
                    },
                },
            };
        },
        computed: {
            currentValue() {
                if (this.value) {
                    return this.value.toDate();
                }

                return null;
            },
        },
        methods: {
            /**
             * Proxy the change event, converting the Date object from el-date-picker to a moment object
             *
             * @param localDateTime - The picked date
             */
            handleChange(localDateTime) {
                let date = dateToMoment(localDateTime, this.type);
                this.$emit('input', date);
            },
        },
    };
</script>
